import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Award, Shield, ArrowRight } from 'lucide-react';

export const FamilyPhysicianAbout: React.FC = () => {
  const values = [
    {
      title: "Patient-Centered Care",
      description: "We put our patients' needs first in everything we do, focusing on personalized care that addresses your unique health concerns.",
      icon: <Heart className="h-8 w-8 text-primary-600" />
    },
    {
      title: "Excellence",
      description: "We strive for excellence in all aspects of our practice, from medical care to customer service and facility management.",
      icon: <Award className="h-8 w-8 text-primary-600" />
    },
    {
      title: "Integrity",
      description: "We maintain the highest standards of professional ethics and conduct in all our interactions with patients and colleagues.",
      icon: <Shield className="h-8 w-8 text-primary-600" />
    },
    {
      title: "Compassion",
      description: "We approach each patient with empathy, understanding, and respect, recognizing the human aspect of healthcare.",
      icon: <Users className="h-8 w-8 text-primary-600" />
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Our Practice</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Learn about our history, mission, and the dedicated team behind Family Physician Office.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission & Vision</h2>
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-primary-600">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Mission</h3>
                  <p className="text-gray-600 leading-relaxed">
                    To provide exceptional, comprehensive healthcare in a compassionate and welcoming 
                    environment. We are committed to the health and well-being of our community, focusing on 
                    preventive care and patient education.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-accent-500">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Vision</h3>
                  <p className="text-gray-600 leading-relaxed">
                    To be the trusted healthcare partner for families in our community, known for clinical 
                    excellence, compassionate care, and innovative approaches to improving health outcomes.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/3259629/pexels-photo-3259629.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop" 
                alt="Medical team in discussion" 
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These values guide everything we do and shape how we care for our patients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="mb-4 flex justify-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    {value.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Join Our Family of Patients</h2>
          <p className="text-xl text-primary-100 mb-8">
            We're accepting new patients and would be honored to care for you and your family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/templates/family-physician#appointment" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Book an Appointment
            </Link>
            <Link to="/templates/family-physician#contact" className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};